import React, { useState } from 'react';
import { Box, Typography, TextField, Button, Container, Alert } from '@mui/material';
import { Link } from 'react-router-dom';
import { verifyCode, login } from '../../Api/index.ts';

export default function VerifyIdentity({ email, formData, onVerify, onResend, onGoBack, title = "Verify Your Identity" }) {
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [resendMessage, setResendMessage] = useState('');
  const [loading, setLoading] = useState(false);

  // Check if this is a login flow (no first_name, last_name, school) or account creation flow
  const isLoginFlow = !formData.first_name && !formData.last_name && !formData.school;

  const handleVerify = async () => {
    if (!code.trim()) {
      setCodeError(true);
      setErrorMessage('Code is required');
      return;
    }
    
    if (code.trim().length !== 4) {
      setCodeError(true);
      setErrorMessage('Code must be 4 digits');
      return;
    }

    if (!/^\d+$/.test(code.trim())) {
      setCodeError(true);
      setErrorMessage('Code must contain only digits');
      return;
    }

    setLoading(true);
    setCodeError(false);
    setErrorMessage('');

    try {
      let response;
      
      if (isLoginFlow) {
        // Login flow - use login API
        response = await login({
          email: email,
          code: code.trim()
        });
      } else {
        // Account creation flow - use verifyCode API
        const verificationData = {
          email: email,
          code: code.trim(),
          first_name: formData.first_name,
          last_name: formData.last_name,
          school: formData.school
        };
        response = await verifyCode(verificationData);
      }
      
      if (response.error) {
        setErrorMessage(response.error);
      } else {
        // Success - call the onVerify callback
        onVerify();
      }
    } catch (error) {
      setErrorMessage('Failed to verify code. Please try again.');
      console.error('Error verifying code:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = () => {
    setResendMessage('Code has been resent.');
    onResend?.();
  };

  return (
    <Container maxWidth="md" sx={{ mt: 8, display: 'flex' }}>
      {/* Left Side - Hidden on mobile */}
      <Box sx={{ 
        flex: 1, 
        pr: 8,
        display: { xs: 'none', md: 'block' }
      }}>
        <Typography variant="h3" sx={{ 
          fontWeight: 'bold', 
          mb: 4, 
          fontSize: { xs: '2rem', md: '2.5rem' },
          color: '#000000'
        }}>
          Buy, Sell Textbooks with Ease—Connecting Students for a More Affordable Education!
        </Typography>

        <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 3 }}>
          <Box sx={{ 
            color: '#6B89FF', 
            fontSize: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            backgroundColor: 'rgba(107, 137, 255, 0.1)',
            boxShadow: '0 2px 8px rgba(107, 137, 255, 0.2)'
          }}>✓</Box>
          <Typography variant="h6" sx={{ 
            fontSize: { xs: '1.1rem', md: '1.3rem' },
            fontWeight: '500',
            color: '#2c3e50'
          }}>
            10,000+ textbooks available for exchange
          </Typography>
        </Box>

        <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 3 }}>
          <Box sx={{ 
            color: '#6B89FF', 
            fontSize: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            backgroundColor: 'rgba(107, 137, 255, 0.1)',
            boxShadow: '0 2px 8px rgba(107, 137, 255, 0.2)'
          }}>✓</Box>
          <Typography variant="h6" sx={{ 
            fontSize: { xs: '1.1rem', md: '1.3rem' },
            fontWeight: '500',
            color: '#2c3e50'
          }}>
            Buy and sell textbooks directly with fellow students
          </Typography>
        </Box>

        <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 3 }}>
          <Box sx={{ 
            color: '#6B89FF', 
            fontSize: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            backgroundColor: 'rgba(107, 137, 255, 0.1)',
            boxShadow: '0 2px 8px rgba(107, 137, 255, 0.2)'
          }}>✓</Box>
          <Typography variant="h6" sx={{ 
            fontSize: { xs: '1.1rem', md: '1.3rem' },
            fontWeight: '500',
            color: '#2c3e50'
          }}>
            Get the books you need for less—no middleman, no hassle
          </Typography>
        </Box>
      </Box>

      {/* Right Side - Full width on mobile */}
      <Box sx={{ 
        flex: 1,
        width: { xs: '100%', md: 'auto' }
      }}>
        <Typography variant="h4" sx={{ 
          mb: 1, 
          fontWeight: 'bold', 
          textAlign: 'center',
          color: '#000000'
        }}>
          {title}
        </Typography>
        <Typography sx={{ 
          mb: 4, 
          textAlign: 'center', 
          color: '#666',
          fontSize: '1.1rem'
        }}>
          We've sent a 4-digit code to:
        </Typography>

        <Typography sx={{ 
          mb: 4, 
          fontWeight: 'medium', 
          textAlign: 'center',
          color: '#000000',
          fontSize: '1.1rem'
        }}>
          {email}
        </Typography>

        {errorMessage && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessage}
          </Alert>
        )}

        {resendMessage && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {resendMessage}
          </Alert>
        )}

        <Box component="form" sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          padding: 3,
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(107, 137, 255, 0.1)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(107, 137, 255, 0.1)'
        }}>
          <Box sx={{ height: '56px' }}>
            <TextField
              fullWidth
              label="Enter the 4-digit code"
              variant="outlined"
              placeholder="Enter code"
              value={code}
              onChange={(e) => {
                setCode(e.target.value);
                setCodeError(false);
                setErrorMessage('');
              }}
              error={codeError}
              helperText={codeError ? errorMessage : " "}
              inputProps={{
                maxLength: 4
              }}
              sx={{
                '& .MuiFormHelperText-root': {
                  position: 'absolute',
                  bottom: '-20px',
                }
              }}
            />
          </Box>

          <Button
            variant="contained"
            sx={{
              mt: 3,
              background: 'linear-gradient(135deg, #6B89FF 0%, #5A75E6 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5A75E6 0%, #4A65D6 100%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 25px rgba(107, 137, 255, 0.3)'
              },
              textTransform: 'none',
              borderRadius: '30px',
              height: '56px',
              fontSize: '18px',
              fontWeight: '600',
              width: '250px',
              display: 'block',
              mx: 'auto',
              boxShadow: '0 4px 15px rgba(107, 137, 255, 0.2)',
              transition: 'all 0.3s ease',
              '&:disabled': {
                backgroundColor: '#ccc',
                transform: 'none',
                boxShadow: 'none'
              }
            }}
            onClick={handleVerify}
            disabled={loading}
          >
            {loading ? 'Verifying...' : 'Continue'}
          </Button>

          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Button
              variant="text"
              onClick={onGoBack}
              sx={{
                color: '#6B89FF',
                textTransform: 'none',
                fontSize: '14px',
                '&:hover': {
                  backgroundColor: 'transparent',
                }
              }}
            >
              ← Go Back
            </Button>

            <Button
              variant="text"
              onClick={handleResend}
              sx={{
                color: '#6B89FF',
                textTransform: 'none',
                fontSize: '14px',
                '&:hover': {
                  backgroundColor: 'transparent',
                }
              }}
            >
              Resend Code
            </Button>
          </Box>

          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography sx={{ mb: 1 }}>
              Already have an account? <Link to="/login" style={{ color: '#6B89FF', textDecoration: 'none' }}>Sign in</Link>
            </Typography>
          </Box>
        </Box>
      </Box>
    </Container>
  );
} 